import { presenceStore } from "../lib/presence";
import { PresenceAvatars } from "../components/PresenceAvatars";
import { SavedViews } from "../components/SavedViews";
import { downloadView } from "../lib/export";
import { toast } from "sonner";
import { forecastBucket } from "../lib/domain";
import * as Popover from "@radix-ui/react-popover";
import { ChevronRight, ExternalLink, Plus, Search } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import type { Loan, Me } from "../lib/types";
import { useGoals, useLoans, useMe, useUsers, type Scope } from "../lib/queries";
import { useLoanActions } from "../lib/mutations";
import { addMonths, dateShort, monthLabel, monthOf, todayIn } from "../lib/dates";
import { blend } from "../lib/blend";
import { moneyCompact } from "../lib/format";
import { num } from "../lib/decimal";
import { densityStore, useIsDark, useMediaQuery } from "../lib/prefs";
import { buildColumns, type ColCtx } from "../grid/columns";
import { PipelineGrid, type DisplayRow, type GridHandle } from "../grid/PipelineGrid";
import { SummaryBar, type PinInfo } from "../components/SummaryBar";
import { Button, cx, EmptyState, Segmented, Spinner, StagePill, TierChip } from "../components/ui";
import { FORECAST_LABEL, OUTCOME_LABEL, stageTone } from "../lib/stages";
import { stageRows, stageView } from "../lib/stageGroups";
import { snapshotChange, type SnapshotKeys } from "../lib/viewSnapshot";
import { StageStrip } from "../components/StageStrip";
import { EmptyViewNotice, StageSections } from "../components/StageSections";
import { StepSections, StepStrip } from "../components/StepSections";
import { stepRows, stepView, LENDSURE_LOAN_STEPS } from "../lib/loanSteps";
import { previewOn } from "../lib/preview";
import { hasCap, isAssignedOnly, isRmSupport } from "../lib/roles";
import { FUNDED_STAGE, NO_STAGE, pipelineView } from "../lib/loBusiness";
import {
  DESK_HREF,
  pendingDealsLabel,
  shortDate,
  useLoCard,
  usePendingDealLinks,
} from "../lib/crm";
import { LoFilterBar } from "../components/LoFilterBar";
import { PipelineGrouping, type GroupBy } from "../components/PipelineGrouping";

type MonthFilter = "this" | "next" | "later" | "all";

export function defaultScope(me: Me): Scope {
  if (isAssignedOnly(me)) return "assigned";
  if (me.user.role === "ae" || isRmSupport(me) || !hasCap(me, "view_pipeline")) return "mine";
  if (hasCap(me, "view_all_teams")) return "all";
  return "team";
}

function scopeOptions(me: Me): { value: Scope; label: string }[] {
  if (isAssignedOnly(me)) return [{ value: "assigned", label: "Assigned to me" }];
  if (me.user.role === "ae" || isRmSupport(me)) return [{ value: "mine", label: isRmSupport(me) ? "My AEs" : "Mine" }, { value: "team", label: "Team" }];
  if (hasCap(me, "view_all_teams")) return [{ value: "team", label: "Team" }, { value: "all", label: "All" }, { value: "mine", label: "Mine" }];
  return [{ value: "team", label: "Team" }, { value: "mine", label: "Mine" }];
}

const TIER_ORDER: Record<string, number> = { PIN: 0, MONEYBALL: 1, HIGH: 2, TBD: 3 };

export function PipelinePage({ onOpenLoan }: { onOpenLoan: (id: string) => void }) {
  const navigate = useNavigate();
  const me = useMe();
  const presence = presenceStore.use();
  const dark = useIsDark();
  const density = densityStore.use();
  const compact = density === "compact";
  const phone = useMediaQuery("(max-width: 767px)");
  const [params, setParams] = useSearchParams();
  const actions = useLoanActions();
  const gridRef = useRef<GridHandle>(null);

  const rawScope = params.get("scope") as Scope | null;
  const allowedScopes = scopeOptions(me);
  const scope: Scope = rawScope && allowedScopes.some((o) => o.value === rawScope) ? rawScope : defaultScope(me);
  const month = (params.get("month") as MonthFilter) || "this";
  const tiers = (params.get("tier") ?? "").split(",").filter(Boolean);
  const q = params.get("q") ?? "";
  // Active deals open grouped by loan stage (owner: "identify the loans by stages very clearly").
  // "By step" (the lender's loan steps, lib/loanSteps.ts) is Preview only.
  const preview = previewOn(me);
  const groupParam = (params.get("group") as GroupBy) || "stage";
  const group: GroupBy = groupParam === "step" && !preview ? "stage" : groupParam;
  const showClosed = params.get("closed") === "1";
  // Opened from an LO record in Prospecting: that LO's loans, optionally one stage or open ones only.
  const lo = params.get("lo");
  const stageFilter = params.get("stage");
  const openOnly = params.get("open") === "1";
  const sortParam = params.get("sort");
  const sort = sortParam ? { colId: sortParam.replace(/^-/, ""), dir: (sortParam.startsWith("-") ? -1 : 1) as 1 | -1 } : null;

  const setParam = (k: string, v: string | null) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        if (v === null || v === "") n.delete(k);
        else n.set(k, v);
        return n;
      },
      { replace: true },
    );

  const loansQ = useLoans(scope, null, true, lo);
  const allLoans = useMemo(() => loansQ.data?.loans ?? [], [loansQ.data]);
  const today = todayIn(me.settings.timezone);
  const cur = monthOf(today);
  const next = addMonths(cur, 1);

  const stageByKey = useMemo(() => new Map(me.stages.map((s) => [s.key, s])), [me.stages]);
  const ctx: ColCtx = useMemo(() => ({ stages: me.stages, stageByKey, settings: me.settings, today, compact, presence, userId: me.user.id }), [me, stageByKey, today, compact, presence]);
  const showComp = allLoans.some((l) => l.comp !== null);
  // The LO's name for the heading, from the deal-side LO card of any loan in the view.
  const loCard = useLoCard(allLoans[0]?.id ?? "", !!lo && allLoans.length > 0);
  const view = pipelineView(
    {
      heading: scope === "assigned" ? "Assigned loans" : scope === "mine" ? (isRmSupport(me) ? "My AEs' pipeline" : "My Pipeline") : scope === "team" ? "Team pipeline" : "All pipelines",
      showOwner: scope !== "mine" || me.user.role === "rm_support",
      canCreate: me.user.role !== "executive",
    },
    lo,
    lo && loCard.data?.card?.person_id === lo ? loCard.data.card.full_name : null,
  );
  const allColumns = useMemo(() => buildColumns({ showOwner: view.showOwner, showComp, compact }), [view.showOwner, showComp, compact]);

  const hidden = (params.get("hidden") ?? "").split(",");
  const columns = allColumns.filter((c) => c.id === "file_name" || !hidden.includes(c.id));

  // New loans created in this session stay pinned at the top until the filters change.
  const [sessionNew, setSessionNew] = useState<string[]>([]);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Bumped by "regroup" so loans whose stage changed move to their new stage section.
  const [regroup, setRegroup] = useState(0);

  const inMonth = useCallback(
    (l: Loan) => {
      const rm = l.reporting_month;
      if (month === "all") return true;
      if (month === "this") return rm === cur;
      if (month === "next") return rm === next;
      return !rm || rm > next;
    },
    [month, cur, next],
  );

  const filterKey = JSON.stringify({ scope, month, tiers, q, group, showClosed, sortParam, lo, stageFilter, openOnly });
  // Stable order: re-sort/filter only when filters or the set of loans change, or on "regroup", never under
  // the cursor. Stage sections are frozen the same way, so "]" on a row never moves the next keystroke onto
  // another loan. Regrouping is its own generation, not a filter: it keeps the "new this session" pins.
  const snap = useRef<{ keys: SnapshotKeys; ids: string[]; stages: Map<string, string | null> } | null>(null);
  const idsKey = allLoans
    .map((l) => l.id)
    .sort()
    .join(",");
  const snapKeys: SnapshotKeys = { filter: filterKey, generation: regroup, ids: idsKey };
  const change = snapshotChange(snap.current?.keys ?? null, snapKeys);
  if (!snap.current || change.rebuild) {
    // filters changed → drop the "new this session" pins
    if (change.clearPins && sessionNew.length) queueMicrotask(() => setSessionNew([]));
    const needle = q.trim().toLowerCase();
    const filtered = allLoans.filter(
      (l) =>
        (showClosed || (l.outcome !== "withdrawn" && l.outcome !== "denied")) &&
        inMonth(l) &&
        (tiers.length === 0 || tiers.includes(l.forecast ?? "NONE")) &&
        (!stageFilter || (stageFilter === NO_STAGE ? !l.stage_key : l.stage_key === stageFilter)) &&
        (!openOnly || l.stage_key !== FUNDED_STAGE) &&
        (!needle || [l.file_name, l.loan_number, l.broker, l.owner_name, l.loan_type].some((v) => v?.toLowerCase().includes(needle))),
    );
    const col = sort ? allColumns.find((c) => c.id === sort.colId) : undefined;
    const keyOf = (l: Loan): string | number | null => {
      if (!col) return null;
      if (col.id === "stage_key") return l.stage_key ? (stageByKey.get(l.stage_key)?.sort ?? 99) : null;
      if (col.id === "forecast") return l.forecast ? TIER_ORDER[l.forecast] : null;
      if (["amount", "price", "rev_pct", "revenue_amount"].includes(col.id)) return num(l[col.id as keyof Loan] as string | null);
      if (col.id === "comp") return num(l.comp?.commission ?? null);
      if (col.id === "alerts") return l.alerts.filter((a) => a.severity === "red").length * 10 + l.alerts.filter((a) => a.severity === "amber").length;
      const v = col.raw(l, ctx);
      return col.kind === "date" ? ((l[col.field as keyof Loan] as string | null) ?? null) : v.toLowerCase() || null;
    };
    filtered.sort((a, b) => {
      if (col && sort) {
        const ka = keyOf(a);
        const kb = keyOf(b);
        if (ka === null && kb !== null) return 1;
        if (kb === null && ka !== null) return -1;
        if (ka !== null && kb !== null && ka !== kb) return (ka < kb ? -1 : 1) * sort.dir;
      }
      const ra = a.reporting_month ?? "9999";
      const rb = b.reporting_month ?? "9999";
      if (ra !== rb) return ra < rb ? -1 : 1;
      const ca = a.coe_date ?? "9999";
      const cb = b.coe_date ?? "9999";
      if (ca !== cb) return ca < cb ? -1 : 1;
      return a.file_name.localeCompare(b.file_name);
    });
    // Every loan's stage is frozen, so a loan created this session (pinned, maybe outside the filters) is too.
    snap.current = { keys: snapKeys, ids: filtered.map((l) => l.id), stages: new Map(allLoans.map((l) => [l.id, l.stage_key])) };
  }

  const byId = useMemo(() => new Map(allLoans.map((l) => [l.id, l])), [allLoans]);
  const pinnedNew = sessionNew.map((id) => byId.get(id)).filter((l): l is Loan => !!l);
  const listed = snap.current.ids.filter((id) => !sessionNew.includes(id)).map((id) => byId.get(id)).filter((l): l is Loan => !!l);
  const viewLoans = [...pinnedNew, ...listed];

  // By stage, loans created this session sit at the top of their own stage section (counted there, and
  // offering "regroup" when their stage changes) instead of a separate NEW group.
  const byStage = group === "stage" ? stageView(viewLoans, me.stages, snap.current.stages) : { sections: [], stale: false };
  const byStep = group === "step" ? stepView(viewLoans, me.stages, snap.current.stages) : { sections: [], stale: false };

  const groupSummary = (loans: Loan[]) => {
    const b = blend(loans.map((l) => ({ amount: l.amount, price: l.price })));
    const pinVol = loans
      .filter((l) => forecastBucket(l.forecast, l.stage_key, me.settings.pin_min_stage) === "pin_volume")
      .reduce((a, l) => a + (num(l.amount) ?? 0), 0);
    return [
      `${loans.length} loan${loans.length === 1 ? "" : "s"}`,
      moneyCompact(b.volume) || "$0",
      group === "month" && pinVol ? `PIN ${moneyCompact(pinVol)}` : null,
      `Rev ${moneyCompact(b.revenue) || "$0"}`,
      b.blendedPrice !== null ? `Blended ${b.blendedPrice.toFixed(3)} (${b.blendedRevPct!.toFixed(3)}%)` : "Blended —",
      b.pricedLoans < b.loans ? `priced ${b.pricedLoans} of ${b.loans}` : null,
    ]
      .filter(Boolean)
      .join("  ·  ");
  };

  const rows: DisplayRow[] = (() => {
    if (group === "none") return viewLoans.map((loan) => ({ type: "loan" as const, loan }));
    if (group === "stage") return stageRows(byStage.sections, collapsed, groupSummary, (st) => stageTone(st, dark));
    if (group === "step") return stepRows(byStep.sections, collapsed, groupSummary);
    const groups = new Map<string, { label: string; loans: Loan[]; order: string }>();
    for (const l of viewLoans) {
      let key: string;
      let label: string;
      let order: string;
      if (sessionNew.includes(l.id)) {
        key = "new";
        label = "NEW";
        order = "0";
      } else if (group === "month") {
        key = l.reporting_month ?? "undated";
        label = l.reporting_month ? monthLabel(l.reporting_month).toUpperCase() : "NO DATE YET";
        order = l.reporting_month ?? "9999";
      } else {
        key = l.forecast ?? "none";
        label = l.forecast ? (l.forecast === "MONEYBALL" ? "MONEYBALL" : FORECAST_LABEL[l.forecast].toUpperCase()) : "NO TIER";
        order = String(l.forecast ? TIER_ORDER[l.forecast] : 9);
      }
      const g = groups.get(key) ?? { label, loans: [], order };
      g.loans.push(l);
      groups.set(key, g);
    }
    const out: DisplayRow[] = [];
    for (const [key, g] of [...groups.entries()].sort((a, b) => (a[1].order < b[1].order ? -1 : 1))) {
      const isCollapsed = collapsed.has(key);
      out.push({ type: "group", key, label: g.label, summary: groupSummary(g.loans), collapsed: isCollapsed });
      if (!isCollapsed) g.loans.forEach((loan) => out.push({ type: "loan", loan }));
    }
    return out;
  })();

  // Summary: selection (≥2 rows) or everything in view.
  const selectedLoans = selectedIds.map((id) => byId.get(id)).filter((l): l is Loan => !!l);
  const summaryLoans = selectedLoans.length > 1 ? selectedLoans : viewLoans;

  // PIN vs plan for the month in focus.
  const pinMonth = month === "next" ? next : cur;
  const goalsQ = useGoals(Number(pinMonth.slice(0, 4)), null);
  const pin: PinInfo = useMemo(() => {

    let volume = 0;
    let notApproved = 0;
    for (const l of allLoans) {
      if (l.forecast !== "PIN" || l.reporting_month !== pinMonth || l.outcome === "withdrawn" || l.outcome === "denied") continue;
      if (forecastBucket(l.forecast, l.stage_key, me.settings.pin_min_stage) === "pin_volume") volume += num(l.amount) ?? 0;
      else notApproved += num(l.amount) ?? 0;
    }
    let plan: number | null = null;
    const g = goalsQ.data;
    if (g) {
      const planOf = (months: { month: string; plan: string | null }[]) => num(months.find((m) => m.month.slice(0, 7) === pinMonth.slice(0, 7))?.plan ?? null);
      if (scope === "mine") {
        const ids = isRmSupport(me) ? me.user.supported_ae_ids : [me.user.id];
        const vals = g.rows.filter((r) => r.user_id && ids.includes(r.user_id)).map((r) => planOf(r.months));
        plan = vals.some((v) => v !== null) ? vals.reduce<number>((a, v) => a + (v ?? 0), 0) : null;
      } else plan = planOf(g.team.months);
    }
    return { month: pinMonth, volume, notApproved, plan };
  }, [allLoans, pinMonth, me, goalsQ.data, scope]);

  const onToggleGroup = useCallback((key: string) => {
    setCollapsed((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  }, []);

  const onSort = useCallback(
    (colId: string) => {
      const curSort = params.get("sort");
      setParam("sort", curSort === colId ? `-${colId}` : curSort === `-${colId}` ? null : colId);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [params],
  );

  const createFor = async (ownerId?: string) => {
    const body: Record<string, string | null> = { file_name: "" };
    if (ownerId) body.owner_user_id = ownerId;
    if (month === "this") body.target_month = cur;
    if (month === "next") body.target_month = next;
    if (tiers.length === 1 && tiers[0] !== "NONE") body.forecast = tiers[0];
    const loan = await actions.create(body);
    if (!loan) return;
    setSessionNew((s) => [loan.id, ...s]);
    if (phone) onOpenLoan(loan.id);
    else window.setTimeout(() => gridRef.current?.focusLoan(loan.id, "file_name"), 60);
  };

  const canCreate = view.canCreate;
  // By stage (or step), the strip and every section stay on screen even when nothing is in view.
  const stageBoard = (group === "stage" || group === "step") && !loansQ.isLoading && !loansQ.isError;
  const clearFilters = () => setParams(new URLSearchParams(scope !== defaultScope(me) ? { scope } : {}), { replace: true });
  const needsOwnerPick = me.user.role !== "ae";

  const toolbar = (
    <div className="flex flex-wrap items-center gap-2">
      <h1 className="font-display mr-2 text-[20px] leading-7 font-semibold text-ink">{view.heading}</h1>
      <Segmented label="Scope" value={scope} options={scopeOptions(me)} onChange={(v) => setParam("scope", v)} />
      <Segmented
        label="Reporting month"
        value={month}
        onChange={(v) => setParam("month", v === "this" ? null : v)}
        options={[
          { value: "this", label: monthLabel(cur, true).split(" ")[0], title: `This month (${monthLabel(cur)})` },
          { value: "next", label: monthLabel(next, true).split(" ")[0], title: `Next month (${monthLabel(next)})` },
          { value: "later", label: "Later", title: "After next month, or no date yet" },
          { value: "all", label: "All" },
        ]}
      />
      <div role="group" aria-label="Forecast tier" className="flex items-center gap-1">
        {["PIN", "MONEYBALL", "HIGH", "TBD"].map((t) => {
          const on = tiers.includes(t);
          return (
            <button
              key={t}
              aria-pressed={on}
              onClick={() => setParam("tier", (on ? tiers.filter((x) => x !== t) : [...tiers, t]).join(","))}
              className={cx(
                "focus-ring h-8 rounded-full border px-3 text-[13px] font-medium",
                on ? "border-primary bg-primary text-on-primary" : "border-line bg-surface text-ink-2 hover:text-ink",
              )}
            >
              {FORECAST_LABEL[t]}
            </button>
          );
        })}
      </div>
      <label className="relative flex items-center">
        <Search size={14} className="pointer-events-none absolute left-2.5 text-ink-3" aria-hidden />
        <span className="sr-only">Filter loans</span>
        <input
          type="search"
          value={q}
          onChange={(e) => setParam("q", e.target.value)}
          placeholder="Filter…"
          className="focus-ring h-8 w-40 rounded-md border border-input bg-surface pr-2 pl-7 text-[13px] text-ink placeholder:text-ink-3"
        />
      </label>
      <PipelineGrouping phone={phone} preview={preview} group={group} onChange={(v) => setParam("group", v === "stage" ? null : v)} />
      <label className="flex items-center gap-1.5 text-[13px] text-ink-2">
        <input type="checkbox" checked={showClosed} onChange={(e) => setParam("closed", e.target.checked ? "1" : null)} className="h-4 w-4 accent-[var(--primary)]" />
        Withdrawn/denied
      </label>
      <div className="ml-auto flex items-center gap-2">
        <Button
          onClick={() => {
            const targetLoan = selectedLoans[0] ?? viewLoans[0];
            if (!targetLoan) {
              navigate("/los");
              return;
            }
            const qParams = new URLSearchParams();
            qParams.set("loan", targetLoan.loan_number || targetLoan.id);
            if (targetLoan.stage_key) qParams.set("stage", targetLoan.stage_key);
            if (targetLoan.file_name) qParams.set("name", targetLoan.file_name);
            if (targetLoan.amount) qParams.set("amount", targetLoan.amount);
            if (targetLoan.note_rate) qParams.set("rate", targetLoan.note_rate);
            if (targetLoan.broker) qParams.set("broker", targetLoan.broker);
            navigate(`/los?${qParams.toString()}`);
          }}
        >
          <ExternalLink size={14} /> Open in LSAI LOS
        </Button>
        <SavedViews userId={me.user.id} params={params} onParams={(p) => setParams(p, { replace: true })} columns={allColumns} defaults={{ scope, month, group, closed: showClosed ? "1" : "0", q, tier: tiers.join(",") }} />
        <Button disabled={loansQ.isLoading || loansQ.isError} onClick={() => void downloadView(rows.filter((r) => r.type === "loan").map((r) => r.loan), columns, ctx).catch((e) => toast.error("Export not created", { description: e instanceof Error ? e.message : "The export could not be recorded, so no file was made." }))}>Export .xlsx</Button>
        {canCreate ? needsOwnerPick ? <NewLoanForOwner me={me} onPick={createFor} /> : (
          <Button variant="primary" onClick={() => void createFor()}>
            <Plus size={16} /> New loan
          </Button>
        ) : null}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-3 px-4 pt-3 pb-3 md:h-full md:min-h-0 md:px-5">
      {toolbar}
      {lo ? <LoFilterBar me={me} lo={lo} stage={stageFilter} openOnly={openOnly} createHint={view.createHint} onClear={clearFilters} /> : null}
      <PendingDealLinksBanner me={me} lo={lo} scope={scope} />
      <SummaryBar
        loans={summaryLoans}
        selection={selectedLoans.length > 1}
        pin={view.showPlan ? pin : null}
        label={view.summaryLabel}
        target={Number(me.settings.price_target) || 103}
        onClearSelection={() => setSelectedIds([])}
      />
      {stageBoard && group === "step" ? (
        <StepStrip
          sections={byStep.sections}
          stale={byStep.stale}
          onRegroup={() => setRegroup((n) => n + 1)}
          onJump={(key) => {
            if (phone) document.getElementById(`step-section-${key}`)?.scrollIntoView({ block: "start", behavior: "smooth" });
            else gridRef.current?.scrollToGroup(`step:${key}`);
          }}
        />
      ) : null}
      {stageBoard && group === "stage" ? (
        <StageStrip
          sections={byStage.sections}
          stale={byStage.stale}
          onRegroup={() => setRegroup((n) => n + 1)}
          onJump={(key) => {
            if (phone) document.getElementById(`stage-section-${key}`)?.scrollIntoView({ block: "start", behavior: "smooth" });
            else gridRef.current?.scrollToGroup(`stage:${key}`);
          }}
        />
      ) : null}
      {stageBoard && viewLoans.length === 0 ? <EmptyViewNotice noLoans={allLoans.length === 0 && !lo} onClearFilters={clearFilters} /> : null}
      <div className="relative min-h-[50vh] flex-1 overflow-hidden rounded-[10px] border border-subtle bg-surface shadow-[var(--shadow-1)] md:min-h-0">
        {loansQ.isLoading ? (
          <div className="flex h-full items-center justify-center gap-2 text-ink-2">
            <Spinner /> Loading your pipeline…
          </div>
        ) : loansQ.isError ? (
          <EmptyState title="Couldn't load the pipeline">
            {(loansQ.error as Error).message}{" "}
            <button className="text-link underline" onClick={() => void loansQ.refetch()}>
              Retry
            </button>
          </EmptyState>
        ) : viewLoans.length === 0 && !stageBoard ? (
          <EmptyState title={allLoans.length === 0 && !lo ? "No loans yet" : "No loans match these filters"}>
            {allLoans.length === 0 && !lo ? (
              <>Press <b>New loan</b> to add one, then paste rows straight from Excel with ⌘V.</>
            ) : (
              <button className="text-link underline" onClick={clearFilters}>
                Clear filters
              </button>
            )}
          </EmptyState>
        ) : phone ? (
          group === "stage" ? (
            <StageSections sections={byStage.sections} renderItems={(loans) => <MobileList loans={loans} me={me} onOpen={onOpenLoan} />} />
          ) : group === "step" ? (
            <StepSections sections={byStep.sections} renderItems={(loans) => <MobileList loans={loans} me={me} onOpen={onOpenLoan} showOutcome />} />
          ) : (
            <MobileList loans={viewLoans} me={me} onOpen={onOpenLoan} />
          )
        ) : (
          <PipelineGrid
            ref={gridRef}
            rows={rows}
            columns={columns}
            ctx={ctx}
            dark={dark}
            sort={sort}
            onSort={onSort}
            onOpen={onOpenLoan}
            onToggleGroup={onToggleGroup}
            onSelectionChange={setSelectedIds}
          />
        )}
      </div>
      {group === "step" ? (
        <p className="text-[12px] text-ink-3">
          <b className="font-semibold text-ink-2">Preview.</b> Sections are {LENDSURE_LOAN_STEPS.lender}&rsquo;s loan steps; each loan sits in the step its stage maps to, shown on its stage chip. Step names and placement are a draft to confirm with the RM.
        </p>
      ) : null}
      {!phone ? (
        <p className="text-[12px] text-ink-3">
          Type to edit — every cell saves on its own. ⌘V pastes from Excel · drag the corner to fill down · Space opens a loan · ] / [ moves the stage · read-only rows show a lock.
        </p>
      ) : null}
    </div>
  );
}

function PendingDealLinksBanner({ me, lo, scope }: { me: Me; lo: string | null; scope: Scope }) {
  const pending = usePendingDealLinks(me, lo, scope);
  if (pending.length === 0) return null;
  const deskAvailable = Boolean(me.user.desk_available);
  return (
    <section
      aria-label="Deals waiting for a scenario"
      className="rounded-lg border border-subtle bg-raised px-3.5 py-2.5 text-[13px]"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-semibold text-ink">
          {pendingDealsLabel(pending.length)}
        </span>
        {deskAvailable ? (
          <Link
            to={DESK_HREF}
            reloadDocument
            className="focus-ring inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1 text-[12px] font-semibold text-on-primary hover:opacity-90"
          >
            Open the Desk
          </Link>
        ) : (
          <span className="text-[12px] text-ink-3">Desk unavailable</span>
        )}
      </div>
      <ul className="mt-1.5 divide-y divide-subtle text-[12px] text-ink-2">
        {pending.map((item) => {
          const href = item.person_id
            ? `/prospecting/people/${item.person_id}`
            : item.company_id
              ? `/prospecting/companies/${item.company_id}`
              : null;
          return (
            <li
              key={item.link.id}
              className="flex flex-wrap items-center justify-between gap-2 py-1"
            >
              <span>
                {href ? (
                  <Link
                    to={href}
                    className="font-medium text-link hover:underline"
                  >
                    {item.name}
                  </Link>
                ) : (
                  <span className="font-medium text-ink">{item.name}</span>
                )}
                {item.company_name && item.company_name !== item.name
                  ? ` · ${item.company_name}`
                  : ""}{" "}
                · started {shortDate(item.link.created_at)} · waiting for a
                scenario
              </span>
              {deskAvailable ? (
                <Link
                  to={DESK_HREF}
                  reloadDocument
                  className="focus-ring font-semibold text-link hover:underline"
                >
                  Open the Desk
                </Link>
              ) : (
                <span className="text-ink-3">Desk unavailable</span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function NewLoanForOwner({ me, onPick }: { me: Me; onPick: (ownerId: string) => void }) {
  const users = useUsers();
  const [open, setOpen] = useState(false);
  const eligible = (users.data ?? []).filter((u) => {
    if (u.role !== "ae" || u.status !== "active") return false;
    if (isRmSupport(me)) return me.user.supported_ae_ids.includes(u.id);
    if (hasCap(me, "manage_all_teams")) return true;
    return u.team_id === me.user.team_id || me.teams.some((t) => t.id === u.team_id);
  });
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button variant="primary">
          <Plus size={16} /> New loan
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="end" sideOffset={6} className="z-50 w-64 rounded-[10px] border border-line bg-raised p-1 shadow-[var(--shadow-2)]">
          <div className="eyebrow px-2 pt-1.5 pb-1 text-ink-3">New loan for…</div>
          {users.isLoading ? <div className="px-2 py-2 text-ink-3">Loading…</div> : null}
          {eligible.map((u) => (
            <button
              key={u.id}
              className="focus-ring flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-[14px] text-ink hover:bg-hover"
              onClick={() => {
                setOpen(false);
                onPick(u.id);
              }}
            >
              {u.display_name}
              <ChevronRight size={14} className="text-ink-3" />
            </button>
          ))}
          {!users.isLoading && eligible.length === 0 ? <div className="px-2 py-2 text-[13px] text-ink-3">No AEs you can create loans for.</div> : null}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

// ── Phone: card list with quick stage / COE / note edits ─────────────────────

type CardOpts = { showOutcome?: boolean };

function MobileList({ loans, me, onOpen, ...opts }: { loans: Loan[]; me: Me; onOpen: (id: string) => void } & CardOpts) {
  const [openId, setOpenId] = useState<string | null>(null);
  return (
    <ul className="divide-y divide-subtle">
      {loans.map((l) => (
        <MobileCard key={l.id} loan={l} me={me} expanded={openId === l.id} onToggle={() => setOpenId((x) => (x === l.id ? null : l.id))} onOpen={onOpen} {...opts} />
      ))}
    </ul>
  );
}

function MobileCard({ loan, me, expanded, onToggle, onOpen, showOutcome }: { loan: Loan; me: Me; expanded: boolean; onToggle: () => void; onOpen: (id: string) => void } & CardOpts) {
  const navigate = useNavigate();
  const dark = useIsDark();
  const actions = useLoanActions();
  const stage = me.stages.find((s) => s.key === loan.stage_key);
  const tone = stageTone(stage, dark);
  const today = todayIn(me.settings.timezone);
  const nextStage = me.stages.find((s) => s.sort === (stage?.sort ?? 0) + 1);
  const [note, setNote] = useState(loan.notes ?? "");
  const red = loan.alerts.filter((a) => a.severity === "red").length;
  const amber = loan.alerts.filter((a) => a.severity === "amber").length;
  return (
    <li data-loan-id={loan.id} className="relative" style={{ background: tone?.tint }}>
      <span className="absolute inset-y-0 left-0 w-1" style={{ background: tone?.solid ?? "transparent" }} aria-hidden />
      <button className="focus-ring flex min-h-[64px] w-full items-start gap-3 py-3 pr-4 pl-4 text-left" aria-expanded={expanded} onClick={onToggle}>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-[15px] font-semibold text-ink">{loan.file_name || "Untitled loan"}</span>
            <PresenceAvatars loanId={loan.id} userId={me.user.id} />
            {red ? <span className="rounded-full bg-danger-solid px-1.5 text-[11px] font-bold text-on-danger">{red}</span> : null}
            {amber ? <span className="rounded-full bg-warning-bg px-1.5 text-[11px] font-bold text-warning">{amber}</span> : null}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[13px] text-ink-2 tnum">
            <span>{moneyCompact(loan.amount) || "no amount"}</span>
            <span>· COE {loan.coe_date ? dateShort(loan.coe_date, today) : "—"}</span>
            {loan.revenue_amount ? <span>· Rev {moneyCompact(loan.revenue_amount)}</span> : null}
            {scopeName(loan, me)}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <StagePill stage={stage} total={me.stages.length} compact />
          <TierChip forecast={loan.forecast} />
          {showOutcome && loan.outcome !== "active" ? <span className="rounded-full bg-sunken px-2 py-0.5 text-[12px] font-medium text-ink-2">{OUTCOME_LABEL[loan.outcome]}</span> : null}
        </div>
      </button>
      {expanded ? (
        <div className="space-y-3 border-t border-subtle bg-surface px-4 py-3">
          {loan.can_edit ? (
            <>
              <label className="block text-[13px] font-medium text-ink-2">
                Stage
                <select
                  className="focus-ring mt-1 h-11 w-full rounded-md border border-input bg-surface px-3 text-[15px] text-ink"
                  value={loan.stage_key ?? ""}
                  onChange={(e) => void actions.patch(loan, { stage_key: e.target.value || null })}
                >
                  <option value="">—</option>
                  {me.stages.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.sort}. {s.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-[13px] font-medium text-ink-2">
                COE
                <input
                  type="date"
                  className="focus-ring mt-1 h-11 w-full rounded-md border border-input bg-surface px-3 text-[15px] text-ink"
                  value={loan.coe_date ?? ""}
                  onChange={(e) => void actions.patch(loan, { coe_date: e.target.value || null })}
                />
              </label>
              <label className="block text-[13px] font-medium text-ink-2">
                Note
                <textarea
                  rows={2}
                  className="focus-ring mt-1 w-full rounded-md border border-input bg-surface px-3 py-2 text-[15px] text-ink"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  onBlur={() => note !== (loan.notes ?? "") && void actions.patch(loan, { notes: note.trim() ? note : null })}
                />
              </label>
              <div className="flex gap-2">
                {nextStage ? (
                  <Button variant="primary" className="h-11 flex-1" onClick={() => void actions.patch(loan, { stage_key: nextStage.key })}>
                    Advance → {nextStage.label}
                  </Button>
                ) : null}
                <Button className="h-11" onClick={() => onOpen(loan.id)}>
                  Details
                </Button>
                <Button
                  className="h-11"
                  onClick={() => {
                    const qParams = new URLSearchParams();
                    qParams.set("loan", loan.loan_number || loan.id);
                    if (loan.stage_key) qParams.set("stage", loan.stage_key);
                    if (loan.file_name) qParams.set("name", loan.file_name);
                    if (loan.amount) qParams.set("amount", loan.amount);
                    if (loan.note_rate) qParams.set("rate", loan.note_rate);
                    if (loan.broker) qParams.set("broker", loan.broker);
                    navigate(`/los?${qParams.toString()}`);
                  }}
                >
                  Open in LSAI LOS
                </Button>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-between text-[13px] text-ink-2">
              Read-only for you.
              <Button onClick={() => onOpen(loan.id)}>Details</Button>
            </div>
          )}
        </div>
      ) : null}
    </li>
  );
}

function scopeName(loan: Loan, me: Me) {
  return loan.owner_user_id !== me.user.id ? <span>· {loan.owner_name}</span> : null;
}
